import { removeNulls } from '../utils/Object.utils';
import { generateUniqueId } from '../utils/unique-id';

type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type AccountMeta = {
  name?: string;
  description?: string;
  createdOn?: string;
  [key: string]: unknown;
};

export type Account = {
  id: string;
  meta?: AccountMeta;
  [key: string]: unknown;
};

export type AccountStatus = {
  isLoading?: boolean;
  error?: { code: string; [key: string]: unknown };
  [key: string]: unknown;
};

export type AccountsState = {
  byId?: Record<string, Account>;
  allIds?: string[];
  status?: Record<string, AccountStatus>;
  isLoading?: boolean;
  error?: { code: string; [key: string]: unknown };
  [key: string]: unknown;
};

const baseAccount = (): Account => {
  return {
    id: generateUniqueId(),
    meta: {
      name: 'New account',
      description: 'Newly created account',
      createdOn: new Date().toISOString(),
    },
  };
};

export const newAccount = (props: Partial<Account> = {}): Account => {
  return {
    ...baseAccount(),
    ...props,
  };
};

const initialState: AccountsState = {
  byId: {},
  allIds: [],
  status: {},
};

const accountsById = (
  stateArg: Record<string, Account> | undefined,
  action: ReducerAction
): Record<string, Account> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'ACCOUNTS_LIST_UPDATE': {
      const { account } = action;
      return {
        ...state,
        [account.id]: account,
      };
    }
    case 'ACCOUNTS_ADD_NEW': {
      const { account } = action;
      return {
        ...state,
        [account.id]: account,
      };
    }
    case 'ACCOUNTS_DELETE': {
      const { accountId } = action;
      return removeNulls({
        ...state,
        [accountId]: null,
      }) as Record<string, Account>;
    }
    case 'ACCOUNT_FINISHED_LOADING': {
      const { account } = action;
      return {
        ...state,
        [account.id]: account,
      };
    }
    case 'ACCOUNT_META_UPDATE': {
      const { accountId, accountMeta } = action;
      const account = (state[accountId] || {}) as Account;
      return {
        ...state,
        [account.id]: {
          ...account,
          meta: {
            ...account.meta,
            ...accountMeta,
          },
        },
      };
    }
    default: {
      return state;
    }
  }
};

const allAccounts = (state: string[] = [], action: ReducerAction): string[] => {
  switch (action.type) {
    case 'ACCOUNTS_ADD_NEW': {
      const { account } = action;
      return state.concat([account.id]);
    }
    case 'ACCOUNTS_DELETE': {
      const { accountId } = action;
      return state.filter((id) => {
        return id !== accountId;
      });
    }
    case 'ACCOUNT_FINISHED_LOADING': {
      const { account } = action;
      return [
        ...state.filter((id) => {
          return id !== account.id;
        }),
        account.id,
      ];
    }
    default:
      return state;
  }
};

const statusReducer = (
  stateArg: Record<string, AccountStatus> | undefined,
  action: ReducerAction
): Record<string, AccountStatus> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'ACCOUNT_IS_LOADING': {
      const { accountId } = action;
      return {
        ...state,
        [accountId]: {
          isLoading: true,
        },
      };
    }
    case 'ACCOUNT_FINISHED_LOADING': {
      const { account } = action;
      return {
        ...state,
        [account.id]: {
          isLoading: false,
        },
      };
    }
    case 'ACCOUNT_FINISHED_LOADING_WITH_ERROR': {
      const { accountId, error } = action;
      return {
        ...state,
        [accountId]: {
          error: error || { code: 'Unknown' },
        },
      };
    }
    default: {
      return state;
    }
  }
};

const accountsReducer = (
  state: AccountsState = initialState,
  action: ReducerAction
): AccountsState => {
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'ACCOUNTS_LIST_IS_LOADING': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'ACCOUNTS_LIST_FINISHED_LOADING': {
      return {
        ...(action.payload || initialState),
        isLoading: false,
      };
    }
    case 'ACCOUNTS_LIST_FINISHED_LOADING_WITH_ERROR':
    case 'ACCOUNTS_FINISHED_ADDING_WITH_ERROR':
    case 'ACCOUNTS_FINISHED_DELETING_WITH_ERROR': {
      const error = action.payload.error || { code: 'Unknown' };
      return {
        ...state,
        error,
        isLoading: false,
      };
    }
    default: {
      return {
        ...state,
        byId: accountsById(state.byId, action),
        allIds: allAccounts(state.allIds, action),
        status: statusReducer(state.status, action),
      };
    }
  }
};

export default accountsReducer;
